import type { InsertDraftStepBetweenRequest } from '@bliks-io/contracts';
import { resolveCurrentLaneKey } from '../../graph/resolveLane';
import { transformDraftNode } from '@/api/draftGraph';
import {
  addSourceToNodeInputs,
  swapSourceOnNode
} from '../../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers
} from '../../../../../../api/mutations/helpers/placeholderStep';
import { mintTempDraftStepId } from '../../../../../../api/mutations/useProcessDraft/shared';
import { insertDraftStepBetweenApi } from '../../../../../../api/persist/draft';
import { collectDownstreamOf, findStepByStableId } from '../shared';
import { ensureNotTerminal } from '../successorInvariant';
import type { EdgeActionDeps } from '../types';

export async function insertBetweenTaskForkBeforeGateway(
  sourceStepId: string,
  position: { x: number; y: number },
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  const allSteps = deps.getCachedSteps();
  const source = findStepByStableId(allSteps, sourceStepId);
  if (!source) return;
  // Which downstream nodes the fork currently feeds is editor semantics —
  // captured client-side and handed to the endpoint as explicit targets.
  const captured = collectDownstreamOf(String(source.stableStepId), allSteps, sourceStepId);
  if (captured.length === 0) return;

  const laneHint = resolveCurrentLaneKey(source, allSteps, deps.getAssigneeLookup?.());
  const capturedTargetIds = new Set(captured.map((c) => c.targetId));

  // Optimistic-only: the insert-between endpoint clears S's ended marker
  // in-transaction (`clearEndedSuccessorStatus`), so no separate PATCH fires.
  ensureNotTerminal(sourceStepId, undefined, deps);

  const performInsert = async (): Promise<string> => {
    const tempId = mintTempDraftStepId();
    const reconcilers = envelopePlaceholderReconcilers(tempId);
    const body: InsertDraftStepBetweenRequest = {
      source: { kind: 'node', stableStepId: sourceStepId },
      targetStableStepIds: [...capturedTargetIds],
      position,
      ...(laneHint ? { laneKey: laneHint } : {})
    };

    const envelope = await deps.runTransaction({
      optimistic: (prev) => {
        const placeholder = addSourceToNodeInputs(
          buildPlaceholderStep({
            tempId,
            processId,
            draftId: prev.draft._id,
            laneKey: laneHint
          }),
          sourceStepId
        );
        let rewired = prev;
        for (const targetId of capturedTargetIds) {
          rewired = transformDraftNode(rewired, targetId, (entry) =>
            entry.kind === 'event' ? entry.node : swapSourceOnNode(entry.node, sourceStepId, tempId)
          );
        }
        return {
          ...rewired,
          draft: { ...rewired.draft, updatedAt: new Date() },
          steps: [...rewired.steps, placeholder]
        };
      },
      optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
      // ONE transactional call: M is created and every captured downstream
      // target (steps and assignments) is rewired onto it atomically.
      persist: () => insertDraftStepBetweenApi(processId, body),
      reconcile: reconcilers.reconcile,
      reconcileLayout: reconcilers.reconcileLayout
    });

    return String(envelope.createdNodeIds[0]);
  };

  await performInsert();
}
